<template>
  <QDialog v-model="isOpen" @hide="afterLeave">
    <QCard>
      <div class="modal">
        <div class="modal__header-link-wrapper">
          <iconClose class="btn gray-btn" @click="close()" />
        </div>
        <h2 class="modal__title">Введите код из письма</h2>

        <div class="modal__field">
          <div class="column">
            <div class="modal__code-wrapper h-middle">
              <div
                class="modal__code-input"
                v-for="(field, index) in form.data.emailCode"
                :key="index"
              >
                <QInput
                  ref="emailCodeRef"
                  outlined
                  v-model="form.data.emailCode[index]"
                  @update:model-value="(val) => emailCodeChanged(val, index)"
                  @paste="handlePaste($event, index)"
                  @focus="setCurrentInputIndex(index)"
                  maxlength="1"
                ></QInput>
              </div>
            </div>
            <span class="modal__timer">
              <span v-if="form.data.emailCodeTimer !== 0">
                Через
                <span class="link">{{ form.data.emailCodeTimer }} сек</span>
                можно получить повторно
              </span>
              <span v-if="form.data.emailCodeTimer === 0"
                >Не пришел код?
                <span @click="sendCodeAgain()" class="link"
                  >Запросить повторно</span
                ></span
              >
            </span>
          </div>
        </div>
        <div class="modal__field">
          <div class="column">
            <button
              class="btn btn-primary"
              :disabled="!isEmailCodeValid || state.preloader"
              @click="sendCode"
            >
              Продолжить
            </button>
          </div>
        </div>
      </div>
    </QCard>
  </QDialog>
</template>

<script lang="ts">
interface IState {
  preloader: boolean;
}

interface IForm {
  data: IDataType;
}

interface IDataType {
  email: string;
  password: string;
  repeatPassword: string;
  emailCode: Record<number, string>;
  emailCodeTimer: number;
}

const initData = () => ({
  email: '',
  password: '',
  repeatPassword: '',
  emailCode: initEmailCode(),
  emailCodeTimer: 0,
});

const initEmailCode = () => ({
  0: '',
  1: '',
  2: '',
  3: '',
  4: '',
  5: '',
});

import { defineComponent, ref, reactive, computed } from 'vue';
import iconClose from '@/components/icons/iconClose.vue';
import iconCircleCheck from '@/components/icons/iconCircleCheck.vue';
import { QDialog, QCard, QInput, QCheckbox } from 'quasar';
import { useAuthStore } from '@/store/authStore';
import iconArrowLeft from '@/components/icons/iconArrowLeft.vue';
import { verifyAccount, getVerifyCode } from '@/api/authService';
import { showError, showMessage } from '@/utils/errorsInterceptor';

export default defineComponent({
  name: 'SubmitAccountModal',
  components: {
    iconClose,
    iconCircleCheck,
    iconArrowLeft,
    QDialog,
    QCard,
    QInput,
    QCheckbox,
  },
  emits: ['close', 'openAuthModal'],
  props: {},

  setup(props, { emit }) {
    const isOpen = ref(false);
    const state: IState = reactive({
      preloader: false,
    });
    const store = useAuthStore();
    const step = ref(1);
    const form = reactive<IForm>({
      data: initData(),
    });

    const currentInputIndex = ref(0);
    const timer = ref(0);

    const isEmailCodeValid = computed((): boolean => {
      let res = true;
      for (let key in form.data.emailCode) {
        if (form.data.emailCode[key] === '') {
          res = false;
        }
      }
      return res;
    });

    const emailCodeChanged = (value, index) => {
      if (value !== '' && value !== null) {
        if (emailCodeRef.value[+index + 1]) {
          emailCodeRef.value[+index + 1].focus();
        }
      }
    };
    const handlePaste = (event, index) => {
      // Предотвращаем обычное поведение вставки
      event.preventDefault();
      // Получаем вставляемые данные
      const pastedData = event.clipboardData.getData('text/plain').slice(0, 6); // Ограничиваем длину 6 символами
      // Обновляем значения форм
      for (let key in form.data.emailCode) {
        form.data.emailCode[key] = pastedData[key] || '';
      }
    };

    const setCurrentInputIndex = (index) => {
      currentInputIndex.value = index; // Устанавливаем текущий индекс инпута
    };

    const initEmailTimer = () => {
      form.data.emailCodeTimer = 60;
      timer.value = setInterval(() => {
        if (form.data.emailCodeTimer < 1) {
          clearInterval(timer.value);
          return;
        }
        form.data.emailCodeTimer = form.data.emailCodeTimer - 1;
      }, 1000);
    };
    initEmailTimer();

    const sendCode = async (): Promise<void> => {
      try {
        const params = {
          code: '',
        };
        for (let key in form.data.emailCode) {
          params.code = params.code + form.data.emailCode[key];
        }
        const response = await verifyAccount(params);
        if (response && response.data) {
          state.preloader = false;
          showMessage('Аккаунт активирован');
          store.setUser(response.data);
          close();
        }
      } catch (error) {
        showError(error);
        state.preloader = false;
      }
    };

    const sendCodeAgain = async (): Promise<void> => {
      state.preloader = true;
      try {
        const response = await getVerifyCode();
        state.preloader = false;
        if (response && response.data) {
          if (form.data.emailCodeTimer === 0) {
            initEmailTimer();
          }
        }
      } catch (error) {
        showError(error);
        state.preloader = false;
      }
    };

    const back = (): void => {
      step.value = 1;
      if (emailRef.value) {
        emailRef.value.focus();
      }
    };

    const close = () => {
      reset();
      isOpen.value = false;
    };

    const afterLeave = () => {
      reset();
      emit('close');
    };

    const reset = () => {
      form.data = initData();
      step.value = 1;
    };

    const openAuthModal = (): void => {
      close();
      emit('openAuthModal');
    };
    const emailRef = ref<any | null>(null);
    const emailCodeRef = ref<any | null>(null);

    return {
      isOpen,
      sendCode,
      close,
      afterLeave,
      reset,
      form,
      state,
      emailRef,
      step,
      openAuthModal,
      back,
      emailCodeChanged,
      emailCodeRef,
      isEmailCodeValid,
      currentInputIndex,
      setCurrentInputIndex,
      handlePaste,
      sendCodeAgain,
    };
  },
});
</script>

<style lang="scss" scoped>
.modal {
  max-width: 480px;
  padding: 48px;
  display: flex;
  flex-direction: column;
  &__back {
    display: flex;
    column-gap: 8px;
    margin-left: -19px;
    font-weight: 400;
    font-size: 14px;
    line-height: 19px;
  }
  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;

    &-link-wrapper {
      display: flex;
      align-items: center;
      justify-content: flex-end;
      margin-bottom: 18px;
    }
  }
  &__title {
    font-weight: 700;
    font-size: 23px;
    line-height: 28px;
    letter-spacing: 1px;
  }
  &__field {
    position: relative;
    margin-top: 24px;
    min-width: 384px;
  }
  &__field-error {
    position: absolute;
    bottom: -19px;
    font-size: 12px;
    color: var(--typography-error);
  }
  &__stay-loggined {
    display: flex;
    justify-content: space-between;
    margin-left: -8px;
    align-items: center;
  }
  &__no-accaunt {
    margin: 16px auto 0px auto;
  }

  &__code-input {
    width: 37px;
  }

  &__code-wrapper {
    display: flex;
    flex-wrap: nowrap;
    column-gap: 14px;
    justify-content: center;
  }

  &__timer {
    margin: 16px auto 0px auto;
    min-height: 14px;
  }
  &__success {
    display: flex;
    column-gap: 16px;
    font-size: 16px;
  }
}
</style>
